import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * DashboardNav — glass sidebar (lg+) + horizontal scroll chips (mobile).
 * tabs: [{ id, label, icon, count? }]
 */
export default function DashboardNav({ tabs, activeTab, onChange, isDarkMode, isAr }) {
  const dark = isDarkMode;
  const Chevron = isAr ? ChevronLeft : ChevronRight;

  return (
    <>
      {/* Mobile chips */}
      <div className="lg:hidden -mx-4 px-4 overflow-x-auto no-scrollbar" data-testid="dashboard-nav-mobile">
        <div className="flex gap-2 w-max pb-1">
          {tabs.map((tab) => {
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => onChange(tab.id)}
                className={`inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-xs font-semibold border whitespace-nowrap transition-all ${
                  active
                    ? 'bg-gradient-to-r from-[#1E9BD9] to-[#0284C7] border-transparent text-white shadow-[0_6px_18px_rgba(30,155,217,0.35)]'
                    : dark ? 'border-[#1E9BD9]/20 text-slate-300 bg-white/5' : 'border-[#1E9BD9]/25 text-slate-600 bg-white'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="h-3.5 w-3.5" />
                {tab.label}
                {tab.count > 0 && <span className={`ms-0.5 px-1.5 rounded-full text-[10px] ${active ? 'bg-white/25' : 'bg-[#F5A962]/20 text-[#F59E0B]'}`}>{tab.count}</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Desktop sidebar */}
      <aside className="hidden lg:block w-[260px] shrink-0" data-testid="dashboard-nav-desktop">
        <nav className={`sticky top-24 rounded-3xl p-2.5 overflow-hidden relative ${dark ? 'cgm-glass-dark' : 'cgm-glass-light shadow-sm'}`}>
          <div className="absolute inset-x-6 top-0 cgm-scanline" />
          {tabs.map((tab, i) => {
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => onChange(tab.id)}
                className={`group relative w-full flex items-center gap-3 px-3 py-2.5 rounded-2xl text-start transition-all duration-200 cgm-rise ${
                  active
                    ? 'bg-gradient-to-r from-[#1E9BD9] to-[#0284C7] text-white shadow-[0_8px_20px_rgba(30,155,217,0.35)]'
                    : dark ? 'text-slate-300 hover:bg-[#1E9BD9]/10 hover:text-white' : 'text-slate-600 hover:bg-[#1E9BD9]/8 hover:text-[#0F172A]'
                }`}
                style={{ animationDelay: `${i * 35}ms` }}
                data-testid={`tab-${tab.id}`}
              >
                {active && <span className="absolute inset-y-2 start-0 w-1 rounded-full bg-[#F5A962]" />}
                <span className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 transition-colors ${active ? 'bg-white/20' : dark ? 'bg-white/5 group-hover:bg-[#1E9BD9]/15' : 'bg-slate-100 group-hover:bg-[#1E9BD9]/10'}`}>
                  <tab.icon className={`h-4.5 w-4.5 ${active ? 'text-white' : 'text-[#1E9BD9]'}`} style={{ width: 18, height: 18 }} />
                </span>
                <span className="text-sm font-semibold flex-1 truncate">{tab.label}</span>
                {tab.count > 0 ? (
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${active ? 'bg-white/25 text-white' : 'bg-[#F5A962]/15 text-[#F59E0B]'}`}>{tab.count}</span>
                ) : (
                  <Chevron className={`h-4 w-4 transition-transform ${active ? 'opacity-80' : 'opacity-0 group-hover:opacity-60 ' + (isAr ? 'group-hover:-translate-x-0.5' : 'group-hover:translate-x-0.5')}`} />
                )}
              </button>
            );
          })}
        </nav>
      </aside>
    </>
  );
}
